import type { Board, BoardNode } from './canvasBoard';

/**
 * Magnific-style board history: one user gesture is one undo step.
 * Uploads, progress and derived playback (join/mix) do not push their own step;
 * they are painted onto the snapshots that still show that in-flight action.
 */

const PHASE_MEDIA = new Set(['status', 'error', 'jobId', 'imageUrl', 'progress']);
const PROGRESS_KEYS = new Set(['status', 'progress', 'error', 'partUrls', 'partJobIds']);
const PLAYBACK_KEYS = new Set(['status', 'progress', 'error', 'videoUrl', 'partUrls', 'partJobIds']);
const IMAGE_MEDIA = new Set(['width', 'height', 'phase', 'versions', 'caption', 'captionUrl']);
const VIDEO_MEDIA = new Set(['status', 'progress', 'error', 'clipUrl', 'videoUrl', 'versions', 'partUrls', 'partJobIds']);
const AUDIO_MEDIA = new Set(['status', 'progress', 'error', 'audioUrl', 'versions', 'seconds']);

export type QuietPlan = {
  type: 'quiet';
  inflightIds: string[];
  playbackIds: string[];
  measureIds: string[];
  aspect: boolean;
};

export type BoardChangePlan = { type: 'same' } | { type: 'user' } | QuietPlan;

export function nodeInflight(n: BoardNode): boolean {
  if (n.kind === 'image') {
    const url = n.phase.imageUrl || '';
    return n.phase.status === 'generating' || url.startsWith('blob:');
  }
  if (n.kind === 'video') return n.status === 'generating' || n.status === 'joining';
  if (n.kind === 'audio') return n.status === 'generating';
  return false;
}

function canon(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canon).join(',')}]`;
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canon(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

function changedKeys(a: object, b: object): string[] {
  const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
  const out: string[] = [];
  for (let i = 0; i < keys.length; i += 1) {
    const k = keys[i];
    if (canon((a as Record<string, unknown>)[k]) !== canon((b as Record<string, unknown>)[k])) out.push(k);
  }
  return out;
}

function edgeSig(board: Board): string {
  return board.edges.map((e) => `${e.from}>${e.to}:${e.role === 'ref' ? 'ref' : 'flow'}`).sort().join('|');
}

const VIDEO_PLAYBACK_OMIT = ['x', 'y', 'status', 'progress', 'error', 'videoUrl', 'partUrls', 'partJobIds'];
const IMAGE_MEASURE_OMIT = ['x', 'y', 'width', 'height'];
const AUDIO_STATUS_OMIT = ['x', 'y', 'status', 'progress', 'error'];

function omitKeys(node: BoardNode, keys: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(node)) {
    if (!keys.includes(k) && v !== undefined) out[k] = v;
  }
  return out;
}

/** Identity of the graph ignoring position and derived playback, so a move keeps a later mix. */
function graphSig(board: Board): string {
  const nodes = [...board.nodes]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((n) => {
      if (n.kind === 'video') return omitKeys(n, VIDEO_PLAYBACK_OMIT);
      if (n.kind === 'image') return omitKeys(n, IMAGE_MEASURE_OMIT);
      return omitKeys(n, AUDIO_STATUS_OMIT);
    });
  return canon({ edges: edgeSig(board), nodes, aspectChosen: board.aspectChosen, imageModelId: board.imageModelId });
}

type NodeKind = 'same' | 'user' | 'inflight' | 'progress' | 'playback' | 'measure';

function classifyNode(prev: BoardNode, next: BoardNode): NodeKind {
  if (prev.kind !== next.kind) return 'user';
  const keys = changedKeys(prev, next);
  if (!keys.length) return 'same';
  if (keys.includes('x') || keys.includes('y')) return 'user';

  if (nodeInflight(prev)) {
    const media = prev.kind === 'image' ? IMAGE_MEDIA : prev.kind === 'video' ? VIDEO_MEDIA : AUDIO_MEDIA;
    if (!keys.every((k) => media.has(k))) return 'user';
    if (prev.kind === 'image' && next.kind === 'image' && keys.includes('phase')) {
      const phaseKeys = changedKeys(prev.phase, next.phase);
      if (phaseKeys.some((k) => !PHASE_MEDIA.has(k))) return 'user';
      const urlSame = (prev.phase.imageUrl || '') === (next.phase.imageUrl || '');
      if (nodeInflight(next) && urlSame && phaseKeys.every((k) => k !== 'imageUrl')) {
        if (keys.every((k) => k === 'phase')) return 'progress';
      }
    } else if (nodeInflight(next) && keys.every((k) => PROGRESS_KEYS.has(k))) {
      return 'progress';
    }
    return 'inflight';
  }

  if (prev.kind === 'image' && next.kind === 'image' && keys.every((k) => k === 'width' || k === 'height')) return 'measure';
  if (prev.kind === 'video' && next.kind === 'video' && keys.every((k) => PLAYBACK_KEYS.has(k))) return 'playback';
  if (prev.kind === 'image' && next.kind === 'image' && keys.every((k) => k === 'phase')) {
    const phaseKeys = changedKeys(prev.phase, next.phase);
    if (phaseKeys.every((k) => k === 'status' || k === 'error' || k === 'jobId' || k === 'progress') && nodeInflight(next)) {
      return 'user';
    }
  }
  if ((prev.kind === 'video' || prev.kind === 'audio') && keys.every((k) => PROGRESS_KEYS.has(k)) && nodeInflight(next)) {
    // Joining is derived from a connection. Starting a generate is the user's gesture.
    if (next.kind === 'video' && next.status === 'joining') return 'progress';
    return 'user';
  }
  return 'user';
}

export function planBoardChange(prev: Board, next: Board): BoardChangePlan {
  if (prev.nodes.length !== next.nodes.length || edgeSig(prev) !== edgeSig(next)) return { type: 'user' };
  const prevById = new Map(prev.nodes.map((n) => [n.id, n]));
  if (next.nodes.some((n) => !prevById.has(n.id))) return { type: 'user' };

  const quiet: QuietPlan = { type: 'quiet', inflightIds: [], playbackIds: [], measureIds: [], aspect: false };
  let any = false;
  for (const n of next.nodes) {
    const p = prevById.get(n.id);
    if (!p) return { type: 'user' };
    const kind = classifyNode(p, n);
    if (kind === 'user') return { type: 'user' };
    if (kind === 'same') continue;
    any = true;
    if (kind === 'inflight') quiet.inflightIds.push(n.id);
    else if (kind === 'playback') quiet.playbackIds.push(n.id);
    else if (kind === 'measure') quiet.measureIds.push(n.id);
  }

  const aspectChanged = prev.aspectRatio !== next.aspectRatio;
  const chosenChanged = Boolean(prev.aspectChosen) !== Boolean(next.aspectChosen);
  const modelChanged = (prev.imageModelId || '') !== (next.imageModelId || '');
  if (chosenChanged || modelChanged) return { type: 'user' };
  if (aspectChanged) {
    if (next.aspectChosen) return { type: 'user' };
    quiet.aspect = true;
    any = true;
  }
  if (!any) return { type: 'same' };
  return quiet;
}

function copyInflight(snapshot: BoardNode, fresh: BoardNode): BoardNode {
  if (snapshot.kind === 'image' && fresh.kind === 'image') {
    return {
      ...snapshot,
      width: fresh.width ?? snapshot.width,
      height: fresh.height ?? snapshot.height,
      versions: fresh.versions ?? snapshot.versions,
      caption: fresh.caption ?? snapshot.caption,
      captionUrl: fresh.captionUrl ?? snapshot.captionUrl,
      phase: fresh.phase,
    };
  }
  if (snapshot.kind === 'video' && fresh.kind === 'video') {
    return {
      ...snapshot,
      status: fresh.status,
      progress: fresh.progress,
      error: fresh.error,
      clipUrl: fresh.clipUrl,
      videoUrl: fresh.videoUrl,
      versions: fresh.versions ?? snapshot.versions,
      partUrls: fresh.partUrls,
      partJobIds: fresh.partJobIds,
    };
  }
  if (snapshot.kind === 'audio' && fresh.kind === 'audio') {
    return {
      ...snapshot,
      status: fresh.status,
      progress: fresh.progress,
      error: fresh.error,
      audioUrl: fresh.audioUrl,
      versions: fresh.versions ?? snapshot.versions,
      seconds: fresh.seconds ?? snapshot.seconds,
    };
  }
  return snapshot;
}

/** Write a quiet follow-up (upload, render, mix, measured size) into an older snapshot. */
export function paintBoardSnapshot(snapshot: Board, next: Board, plan: QuietPlan): Board {
  const freshById = new Map(next.nodes.map((n) => [n.id, n]));
  let board = snapshot;
  if (plan.inflightIds.length || plan.measureIds.length) {
    let changed = false;
    const nodes = board.nodes.map((n) => {
      const fresh = freshById.get(n.id);
      if (!fresh || fresh.kind !== n.kind) return n;
      if (plan.inflightIds.includes(n.id) && nodeInflight(n)) {
        changed = true;
        return copyInflight(n, fresh);
      }
      if (plan.measureIds.includes(n.id) && n.kind === 'image' && fresh.kind === 'image' && !n.width && fresh.width) {
        changed = true;
        return { ...n, width: fresh.width, height: fresh.height };
      }
      return n;
    });
    if (changed) board = { ...board, nodes };
  }
  if (plan.playbackIds.length && graphSig(board) === graphSig(next)) {
    let changed = false;
    const nodes = board.nodes.map((n) => {
      if (n.kind !== 'video' || !plan.playbackIds.includes(n.id)) return n;
      const fresh = freshById.get(n.id);
      if (!fresh || fresh.kind !== 'video') return n;
      changed = true;
      return {
        ...n,
        status: fresh.status,
        progress: fresh.progress,
        error: fresh.error,
        videoUrl: fresh.videoUrl,
        partUrls: fresh.partUrls,
        partJobIds: fresh.partJobIds,
      };
    });
    if (changed) board = { ...board, nodes };
  }
  if (plan.aspect && !board.aspectChosen && next.nodes.every((n) => board.nodes.some((s) => s.id === n.id))) {
    board = { ...board, aspectRatio: next.aspectRatio };
  }
  return board;
}
